/** Cosmetic space weather. Pure planet seed + shared epoch seconds: joining,
 * travelling, saving and frame rate never roll a new event. No gameplay hazard.
 * These compressed durations and magnetic ovals are art direction, not a Kp model. */
import { hashSeed, mulberry32, rngFromString } from './rng.js';
import * as pmath from './pmath.js';
import type { PlanetDef } from './universe.js';

export const AURORA_WINDOW_SECONDS = 5400;
export const AURORA_EVENT_CHANCE = .42;
export interface AuroraConfig {
  enabled: boolean;
  seed: number;
  axis: [number, number, number];
  offset: number;
}
export interface AuroraState {
  window: number;
  start: number;
  duration: number;
  strength: number;
  /** Colatitude of the main oval, radians; stronger storms expand equatorward. */
  oval: number;
  /** Small periodic animation phases. Never upload epoch-sized floats to GLSL. */
  phase: number;
  detailPhase: number;
}
const TAU = Math.PI * 2;
const smooth = (v: number) => { const t = Math.max(0, Math.min(1, v)); return t*t*(3-2*t); };
const phase = (time: number, period: number) => (((time % period) + period) % period) / period * TAU;

export function auroraConfig(def: Pick<PlanetDef, 'seed'|'atmosphere'|'spin'>): AuroraConfig {
  const rng = rngFromString(`${def.seed}:aurora:magnetic:v1`);
  const [x,y,z] = def.spin.axis;
  // Stable perpendicular to the spin axis, then a modest, seed-specific tilt.
  const ex = Math.abs(y) < .9 ? -z : y, ey = Math.abs(y) < .9 ? 0 : -x, ez = Math.abs(y) < .9 ? x : 0;
  const el = Math.sqrt(ex*ex+ey*ey+ez*ez), a = rng()*TAU, tilt = (.12+rng()*.30);
  const u = [ex/el, ey/el, ez/el], v = [y*u[2]-z*u[1], z*u[0]-x*u[2], x*u[1]-y*u[0]];
  const c = pmath.cos(tilt), s = pmath.sin(tilt), ca = pmath.cos(a), sa = pmath.sin(a);
  const axis: [number,number,number] = [x*c+s*(u[0]*ca+v[0]*sa),y*c+s*(u[1]*ca+v[1]*sa),z*c+s*(u[2]*ca+v[2]*sa)];
  const length = Math.sqrt(axis[0]**2+axis[1]**2+axis[2]**2);
  for(let i=0;i<3;i++) axis[i]/=length;
  return { enabled: !!def.atmosphere, seed: hashSeed(`${def.seed}:aurora:events:v1`), axis, offset: rng()*AURORA_WINDOW_SECONDS };
}

export function auroraAt(config: AuroraConfig, time: number, out = {} as AuroraState): AuroraState {
  // Each window contains either no event or a bounded event, separated from its
  // neighbours by quiet margins. Integer hashing works for negative epochs too.
  const valid = Number.isFinite(time); if (!valid) time = 0;
  const window = Math.floor((time+config.offset)/AURORA_WINDOW_SECONDS);
  const rng = mulberry32(hashSeed(`${config.seed}:${window}`));
  const occurs = rng() < AURORA_EVENT_CHANCE;
  const duration = 720+rng()*780;
  const start = window*AURORA_WINDOW_SECONDS-config.offset+300+rng()*(AURORA_WINDOW_SECONDS-duration-600);
  const peak = .58+rng()*.42;
  const elapsed = time-start;
  const envelope = smooth(elapsed/150)*smooth((duration-elapsed)/240);
  const strength = config.enabled && valid && occurs ? peak*envelope : 0;
  Object.assign(out, { window, start, duration, strength, oval: .35+.33*strength,
    phase: phase(time, 720), detailPhase: phase(time, 173) });
  return out;
}

/** Observer sky contrast only. Events continue on the day side unseen. */
export function auroraDarkness(sunHeight: number): number {
  return 1-smooth((sunHeight+.24)/.20);
}
